import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Pagination } from '@mui/material';
import * as TopList from 'components/Layout/Tiles/ListContainer';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { TileContent } from 'components/Layout/PageContainer';
import { Measure } from 'services/model/model.service';
import { DataEntry } from 'models/data';
import { EmptyState } from 'components/EmptyState';
import { TbIcon } from 'components/Figma';

interface TopGrowthListProps<T extends { name: string; entry: DataEntry }> {
  items: T[];
  isComparisonActive: boolean;
  gmvMeasure: Measure;
  getNameCellProps: (item: T) => {
    name: string;
    icon?: React.ReactNode;
    countryCode?: string;
    color: string;
  };
  onClick?: (item: T) => void;
}

export function TopGrowthList<T extends { name: string; entry: DataEntry }>({
  items,
  isComparisonActive,
  gmvMeasure,
  getNameCellProps,
  onClick = undefined,
}: TopGrowthListProps<T>) {
  const [pageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  function calculateGmvGrowthPercentage(item: T): number {
    const current = gmvMeasure.getValue(item.entry.values) ?? 0;
    const previous = gmvMeasure.getValue(item.entry.compareValues) ?? 0;
    return current / previous - 1;
  }

  function calculateAbsoluteGmvGrowth(item: T): number {
    const current = gmvMeasure.getValue(item.entry.values) ?? 0;
    const previous = gmvMeasure.getValue(item.entry.compareValues) ?? 0;
    return current - previous;
  }

  function createListRow(
    item: T & {
      gmvGrowthPercentage: number;
      gmvGrowthAbsolute: number;
    }
  ): React.ReactElement {
    const { name, icon, countryCode, color } = getNameCellProps(item);

    return (
      <TopList.ListRow
        key={item.name}
        onClick={
          onClick
            ? () => {
                onClick(item);
              }
            : undefined
        }
      >
        <TopList.NameCell name={name} icon={icon} countryCode={countryCode?.toLowerCase()} color={color} />
        <TopList.MetricCell
          label="insights.common.growth.percentage"
          value={item.gmvGrowthPercentage}
          unit={MeasureUnitCode.PERCENT}
          size="lg"
        />
        <TopList.MetricCell
          label="insights.common.growth.abs"
          value={item.gmvGrowthAbsolute}
          unit={MeasureUnitCode.CURRENCY}
        />
      </TopList.ListRow>
    );
  }

  if (isComparisonActive) {
    const fastGrowingItems = items
      .filter(item => item.entry.compareValues)
      .map(item => ({
        ...item,
        gmvGrowthPercentage: calculateGmvGrowthPercentage(item),
        gmvGrowthAbsolute: calculateAbsoluteGmvGrowth(item),
      }))
      .filter(item => item.gmvGrowthAbsolute > 0)
      .sort((a, b) => b.gmvGrowthPercentage - a.gmvGrowthPercentage);

    if (fastGrowingItems.length === 0) {
      return <EmptyState />;
    }
    return (
      <TileContent className="flex flex-col justify-between items-center">
        <TopList.ListContainer>
          {fastGrowingItems
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map(item => createListRow(item))}
        </TopList.ListContainer>

        {Math.ceil(fastGrowingItems.length / pageSize) > 1 && (
          <Pagination
            className="mt-4"
            size="small"
            count={Math.ceil(fastGrowingItems.length / pageSize)}
            page={currentPage}
            onChange={(_event, page) => setCurrentPage(page)}
          />
        )}
      </TileContent>
    );
  }

  return (
    <div className="w-6/12 h-3/5 flex items-center justify-center gap-4 p-4 mx-auto">
      <TbIcon name={'InformationOutlined'} className="w-[42px] h-[42px]" />
      <FormattedMessage
        id="insights.common.comparison.no_data"
        defaultMessage="No data available. Please adjust the comparison period to view growth."
      />{' '}
    </div>
  );
}
